import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
    ColorPickerChangeEvent,
    NumericTextBox,
    NumericTextBoxChangeEvent,
    TextBox,
    TextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import React from 'react';
import { ColorPickerWithDefault } from '../../app/components/inputs/ColorPickerWithDefault';
import { AnalyzerRouteAssociatedPoint, ROUTE_ANALYZER_DEFAULT_POINT_COLOR } from './types';

export interface EditPointRingProps {
    editItem?: AnalyzerRouteAssociatedPoint;
    onSave: (item: AnalyzerRouteAssociatedPoint) => void;
    onClose: () => void;
}

const EditPointRing = (props: EditPointRingProps) => {
    const [label, setLabel] = React.useState('');
    const [color, setColor] = React.useState(ROUTE_ANALYZER_DEFAULT_POINT_COLOR);
    const [latitude, setLatitude] = React.useState(0);
    const [longitude, setLongitude] = React.useState(0);
    const [range, setRange] = React.useState(0);

    React.useEffect(() => {
        if (props.editItem) {
            setLabel(props.editItem.label);
            setColor(props.editItem.color);
            setLatitude(props.editItem.latitude);
            setLongitude(props.editItem.longitude);
            setRange(props.editItem.range);
        }
    }, [props.editItem]);

    const handleLabelChange = React.useCallback((event: TextBoxChangeEvent) => setLabel(event.value as string), []);
    const handleColorChange = React.useCallback((event: ColorPickerChangeEvent) => setColor(event.value), []);
    const handleLatitudeChange = React.useCallback((event: NumericTextBoxChangeEvent) => setLatitude(event.value ?? 0), []);
    const handleLongitudeChange = React.useCallback((event: NumericTextBoxChangeEvent) => setLongitude(event.value ?? 0), []);
    const handleRangeChange = React.useCallback((event: NumericTextBoxChangeEvent) => setRange(event.value ?? 0), []);

    const handleSave = () => {
        props.onSave({
            enabled: props.editItem?.enabled ?? true,
            label: label,
            latitude: latitude,
            longitude: longitude,
            range: range,
            color: color
        });
        props.onClose();
    };

    return (
        <Dialog width={400} title="Edit point/ring" onClose={props.onClose}>
            <div className="d-flex flex-column">
                <div className="secondary-text">Label</div>
                <div className="d-flex gap-2">
                    <TextBox placeholder="Label" value={label} onChange={handleLabelChange} />
                    <ColorPickerWithDefault
                        defaultColor={ROUTE_ANALYZER_DEFAULT_POINT_COLOR}
                        views={['palette', 'gradient']}
                        value={color}
                        onChange={handleColorChange}
                    />
                </div>

                <div className="secondary-text pt-2">Latitude</div>
                <NumericTextBox
                    placeholder="Latitude"
                    format={'n5'}
                    step={0.00001}
                    min={-180}
                    max={180}
                    value={latitude}
                    onChange={handleLatitudeChange}
                />

                <div className="secondary-text pt-2">Longitude</div>
                <NumericTextBox
                    placeholder="Longitude"
                    format={'n5'}
                    step={0.00001}
                    min={-180}
                    max={180}
                    value={longitude}
                    onChange={handleLongitudeChange}
                />

                <div className="secondary-text pt-2">Range</div>
                <NumericTextBox placeholder="Range" format={'# miles'} value={range} onChange={handleRangeChange} />

                <div className="pt-2"></div>
            </div>

            <DialogActionsBar layout={'stretched'}>
                <div className="d-flex gap-2 px-3">
                    <Button className="w-50" themeColor={'primary'} onClick={handleSave}>
                        Save
                    </Button>
                    <Button className="w-50" fillMode={'outline'} onClick={props.onClose}>
                        Cancel
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default EditPointRing;
